import { Bell, BellOff, BellRing, Loader2, MoonStar } from "lucide-react";
import { useEffect, useState } from "react";

import { useNotify } from "../core/notify";
import type { PushState } from "../core/push";
import { PUSH_VERIFY_MAX_ATTEMPTS, usePush } from "../core/push";

// The notifications row in the account menu: one switch, one line of state.
// Every state the browser side can be in has a label; the toggle itself is
// only interactive when there is something to do (enable / disable).
function describe(
  state: PushState,
  isIOS: boolean,
  isStandalone: boolean,
  now: number,
): { label: string; detail: string | null } {
  switch (state.status) {
    case "unsupported":
      return { label: "Notifications unavailable", detail: state.reason };
    case "blocked":
      return {
        label: "Notifications blocked",
        detail:
          "Allow notifications for this site in the browser's settings, then flip the switch again.",
      };
    case "disabled":
      return {
        label: "Notifications off",
        detail:
          isIOS && !isStandalone
            ? "On iPhone and iPad, add Lately to the Home Screen first — Safari only delivers notifications to the installed app."
            : "Get a notification when new mail arrives, even with Lately closed.",
      };
    case "subscribing":
      return { label: "Turning on…", detail: "Subscribing with the push service." };
    case "waiting": {
      // The mail server verifies at most one new subscription a minute per
      // account; the registration is made when the minute is up.
      const seconds = Math.max(0, Math.ceil((state.until - now) / 1000));
      return {
        label: "Verifying…",
        detail: `Waiting for the mail server — it accepts a new subscription in ${seconds}s.`,
      };
    }
    case "verifying":
      return {
        label: "Verifying…",
        detail:
          state.attempt > 1
            ? `Waiting for the mail server's verification push (try ${state.attempt} of ${PUSH_VERIFY_MAX_ATTEMPTS}).`
            : "Waiting for the mail server's verification push.",
      };
    case "enabled":
      return {
        label: "Notifications on",
        detail: state.expires
          ? `Renews before ${new Date(state.expires).toLocaleDateString()}.`
          : null,
      };
    case "error":
      return { label: "Notifications failed", detail: state.message };
    default:
      return { label: "Notifications", detail: null };
  }
}

// "Notify me when Lately is closed": with it on, the page keeps an encrypted
// record (accounts, credentials, cursors) in IndexedDB so the service worker
// can run the shared core's digest with no page open — per-thread
// notifications that clear when the mail is read elsewhere. Off (the
// default), a push with no page open shows the generic "New mail" line and
// clears nothing until Lately is opened. Offered only once push itself is on,
// and only when this build's service worker ships the headless core that
// reads the record ("unavailable" = nothing would consume it, so nothing is
// offered or stored).
function ClosedPageToggle() {
  const { controller, state } = useNotify();
  const [busy, setBusy] = useState(false);
  if (state.status === "unsupported" || state.status === "unavailable") {
    return null;
  }
  const on = state.status === "on";
  const detail =
    state.status === "error"
      ? state.message
      : on
        ? "Keeps this device's unlock key so notifications name the sender and clear when you read the mail elsewhere."
        : 'Off: with Lately closed you get a plain "New mail" line and nothing clears until you open it.';
  const toggle = () => {
    setBusy(true);
    (on ? controller.disable() : controller.enable())
      .catch(() => undefined)
      .finally(() => setBusy(false));
  };
  return (
    <div
      data-notify-state={state.status}
      className="flex items-start gap-2 rounded-md px-2.5 py-2 text-left text-sm"
    >
      <MoonStar
        aria-hidden
        className={`mt-0.5 h-4 w-4 shrink-0 ${
          state.status === "error" ? "text-destructive" : "text-muted-foreground"
        }`}
      />
      <span className="min-w-0 flex-1">
        <span className="block font-medium">Notify me when Lately is closed</span>
        <span className="block text-xs text-muted-foreground">{detail}</span>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label="Notify me when Lately is closed"
        disabled={busy}
        onClick={toggle}
        className={`relative mt-0.5 h-5 w-9 shrink-0 rounded-full border transition-colors disabled:opacity-40 ${
          on ? "border-primary bg-primary" : "border-border bg-muted"
        }`}
      >
        <span
          aria-hidden
          className={`absolute top-0.5 h-3.5 w-3.5 rounded-full bg-background shadow transition-transform ${
            on ? "left-0.5 translate-x-4" : "left-0.5 translate-x-0"
          }`}
        />
      </button>
    </div>
  );
}

export function NotificationsToggle() {
  const { controller, state } = usePush();
  const [busy, setBusy] = useState(false);
  // A one-second tick while the countdown is showing.
  const [now, setNow] = useState(() => Date.now());
  const counting = state.status === "waiting";
  useEffect(() => {
    if (!counting) {
      return;
    }
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [counting]);
  const { label, detail } = describe(
    state,
    controller.platform.isIOS,
    controller.platform.isStandalone,
    now,
  );
  const pending =
    state.status === "subscribing" || state.status === "waiting" || state.status === "verifying";
  const on = state.status === "enabled" || pending;
  // "blocked" stays clickable: enable() re-checks the permission, so a user
  // who just re-allowed notifications in the site settings gets straight in.
  const disabled = busy || state.status === "unsupported" || state.status === "subscribing";
  const Icon =
    state.status === "enabled"
      ? BellRing
      : pending
        ? Loader2
        : state.status === "unsupported" || state.status === "blocked"
          ? BellOff
          : Bell;

  const run = (action: Promise<void>) => {
    setBusy(true);
    action.catch(() => undefined).finally(() => setBusy(false));
  };
  const toggle = () => run(on ? controller.disable() : controller.enable());

  return (
    <>
      <div
        data-push-state={state.status}
        className="flex items-start gap-2 rounded-md px-2.5 py-2 text-left text-sm"
      >
        <Icon
          aria-hidden
          className={`mt-0.5 h-4 w-4 shrink-0 ${pending ? "animate-spin" : ""} ${
            state.status === "error" ? "text-destructive" : "text-muted-foreground"
          }`}
        />
        <span className="min-w-0 flex-1">
          <span className="block font-medium">{label}</span>
          {detail ? <span className="block text-xs text-muted-foreground">{detail}</span> : null}
          {state.status === "error" ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => run(controller.enable())}
              className="mt-1 block text-xs font-medium text-primary underline-offset-2 hover:underline disabled:opacity-40"
            >
              Try again
            </button>
          ) : null}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label="Notifications for new mail"
          disabled={disabled}
          onClick={toggle}
          className={`relative mt-0.5 h-5 w-9 shrink-0 rounded-full border transition-colors disabled:opacity-40 ${
            on ? "border-primary bg-primary" : "border-border bg-muted"
          }`}
        >
          <span
            aria-hidden
            className={`absolute top-0.5 h-3.5 w-3.5 rounded-full bg-background shadow transition-transform ${
              on ? "left-0.5 translate-x-4" : "left-0.5 translate-x-0"
            }`}
          />
        </button>
      </div>
      {state.status === "enabled" ? <ClosedPageToggle /> : null}
    </>
  );
}
